import { useRef, useState } from "react";
import { authedFetch } from "../../lib/api-client";

const API_URL = (import.meta.env.VITE_API_URL ?? "/api").replace(/\/$/, "");

export type WordAudioButtonProps = {
  /** Dutch text the TTS pipeline will pronounce. Used as both the cache
   *  key (sha1) on the server and the query param sent to /api/woorden/audio. */
  text: string;
  /** Visible label override; defaults to "Beluister". Ignored for the
   *  icon-only variant (round play affordance). */
  label?: string;
  /** "pill" (default) keeps the original labelled pill so existing callers
   *  are untouched; "icon" renders a round, label-free play button for the
   *  split-pane word list / detail. */
  variant?: "pill" | "icon";
  /** Icon-only sizes: sm 28px (super-zinnen miniplay), md 34px (list row),
   *  lg 52px (detail big play). No effect on the pill variant. */
  iconSize?: "sm" | "md" | "lg";
};

const ICON_DIMENSIONS: Record<NonNullable<WordAudioButtonProps["iconSize"]>, string> = {
  sm: "w-7 h-7",
  md: "w-[34px] h-[34px]",
  lg: "w-[52px] h-[52px]",
};

const GLYPH_DIMENSIONS: Record<NonNullable<WordAudioButtonProps["iconSize"]>, string> = {
  sm: "text-[11px]",
  md: "text-[13px]",
  lg: "text-base",
};

type Status = "idle" | "loading" | "ready" | "error";

type AudioResponse =
  | { ok: true; data: { audioUrl: string; cached: boolean } }
  | { ok: false; error: { code: string; message: string } };

/**
 * Lazy TTS pill for the vocab module. Resolves the audio URL on first
 * tap instead of receiving a `src` upfront — appropriate when there are
 * dozens of potential plays per page and the cache cost should follow
 * the user's actual taps, not the page load.
 *
 * Single-play semantics: pauses every other <audio> on the page before
 * starting.
 *
 * The /woorden/audio route returns `audioUrl` like `/audio/<id>` —
 * relative to the API origin, NOT to the SPA. Prepend the API base so the
 * Vite proxy + Cloud Run path are both consistent.
 */
export function WordAudioButton({
  text,
  label = "Beluister",
  variant = "pill",
  iconSize = "md",
}: WordAudioButtonProps) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [playing, setPlaying] = useState(false);

  function pauseOtherAudio() {
    if (typeof document === "undefined") return;
    document.querySelectorAll("audio").forEach((a) => {
      if (a !== ref.current && !a.paused) a.pause();
    });
  }

  async function resolveSrcOnce(): Promise<string | null> {
    const a = ref.current;
    if (!a) return null;
    if (a.src) return a.src;
    setStatus("loading");
    try {
      const r = await authedFetch(
        `/woorden/audio?text=${encodeURIComponent(text)}`,
      );
      const j = (await r.json()) as AudioResponse;
      if (!j.ok) {
        console.warn(`[WordAudioButton] ${j.error.code}: ${j.error.message}`);
        setStatus("error");
        return null;
      }
      // audioUrl is "/audio/<id>" — bolt it onto API_URL so the Vite
      // proxy + production API origin both work without a path rewrite.
      a.src = `${API_URL}${j.data.audioUrl}`;
      setStatus("ready");
      return a.src;
    } catch (err) {
      console.warn(
        `[WordAudioButton] fetch failed: ${err instanceof Error ? err.message : String(err)}`,
      );
      setStatus("error");
      return null;
    }
  }

  async function toggle() {
    const a = ref.current;
    if (!a) return;
    if (!a.paused) {
      a.pause();
      return;
    }
    const src = await resolveSrcOnce();
    if (!src) return;
    pauseOtherAudio();
    try {
      await a.play();
    } catch {
      // Autoplay block / user-gesture race — caller can tap again.
    }
  }

  const visible =
    status === "loading"
      ? "Laden…"
      : status === "error"
        ? "Niet beschikbaar"
        : playing
          ? "Pauzeer"
          : label;

  return (
    <>
      {variant === "icon" ? (
        <button
          type="button"
          onClick={toggle}
          disabled={status === "loading" || status === "error"}
          aria-label={playing ? `Pauzeer ${text}` : `Speel ${text} af`}
          className={`inline-grid place-items-center rounded-full shrink-0 transition-transform duration-150 ease-out hover:scale-105 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2 ${
            ICON_DIMENSIONS[iconSize]
          } ${
            iconSize === "lg"
              ? "bg-[var(--accent)] text-white shadow-[var(--shadow-blue)]"
              : "bg-[var(--accent-soft)] text-[var(--accent)]"
          } ${GLYPH_DIMENSIONS[iconSize]}`}
        >
          <span aria-hidden className={status === "loading" ? "animate-pulse" : undefined}>
            {playing ? "❙❙" : "▶"}
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={toggle}
          disabled={status === "loading" || status === "error"}
          aria-label={playing ? `Pauzeer ${text}` : `Speel ${text} af`}
          className="inline-flex items-center gap-2 rounded-md border border-[var(--border)] bg-[var(--panel-soft)] hover:bg-[var(--panel)] hover:border-[var(--accent)]/40 px-3 py-1.5 text-xs text-[var(--ink-muted)] hover:text-[var(--ink)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <span aria-hidden className={status === "loading" ? "animate-pulse" : undefined}>
            {playing ? "❙❙" : "▶"}
          </span>
          <span>{visible}</span>
        </button>
      )}
      <audio
        ref={ref}
        preload="none"
        playsInline
        className="sr-only"
        aria-hidden
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />
    </>
  );
}
